import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { getArticleBySlug, getAuthorImage, getRelatedArticles } from "@/data/articles";
import { scrollToTop } from "@/utils";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Clock,
  Quote,
  Share2,
  Tag,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";

const PageArticleDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = article ? getRelatedArticles(article.id, 3) : [];

  useEffect(() => {
    scrollToTop();
  }, [slug]);

  const handleShare = () => {
    if (typeof window !== "undefined") {
      if (navigator.share) {
        navigator
          .share({
            title: article?.title,
            text: article?.subtitle,
            url: window.location.href,
          })
          .catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    }
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans flex flex-col justify-between">
        <Header />
        <main className="pt-32 pb-20 container mx-auto px-4 text-center max-w-xl space-y-4">
          <BookOpen className="w-12 h-12 text-slate-400 mx-auto" />
          <h1 className="text-2xl font-bold text-slate-900">
            Article introuvable
          </h1>
          <p className="text-sm text-slate-600">
            L'article que vous recherchez n'existe pas ou a été déplacé.
          </p>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-xs sm:text-sm font-bold shadow hover:bg-slate-800"
          >
            &larr; Retour aux publications
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const pageUrl = `https://egarizim.com/articles/${article.slug}`;

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col justify-between">
      <Helmet>
        <title>{article.title} | Église de Dieu Garizim</title>
        <meta name="description" content={article.summary} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.summary} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="article:published_time" content={article.isoDate} />
        <meta property="article:author" content={article.author} />
        <meta property="article:section" content={article.category} />
      </Helmet>

      <Header />

      <main className="pt-24 pb-20">
        {/* Breadcrumbs & Navigation */}
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-slate-500 overflow-x-auto whitespace-nowrap">
            <Link to="/" className="hover:text-slate-800 transition-colors">
              Accueil
            </Link>
            <span>/</span>
            <Link
              to="/articles"
              className="hover:text-slate-800 transition-colors"
            >
              Articles
            </Link>
            <span>/</span>
            <span className="text-slate-800 font-semibold truncate max-w-[200px] sm:max-w-none">
              {article.title}
            </span>
          </nav>
        </div>

        {/* Article Article Container */}
        <article className="container mx-auto max-w-4xl px-4 sm:px-6 space-y-8">
          {/* Header Card */}
          <header className="bg-white rounded-3xl p-6 sm:p-10 shadow-lg border border-slate-200/90 space-y-6">
            {/* Meta badges */}
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-church-blue-50 text-church-blue-700 font-bold border border-church-blue-200">
                  {article.category}
                </span>
                <span className="flex items-center gap-1.5 text-slate-500">
                  <Calendar className="w-3.5 h-3.5" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5 text-slate-500">
                  <Clock className="w-3.5 h-3.5" />
                  {article.readTime}
                </span>
              </div>

              {/* Share button */}
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition-colors"
                title="Partager cet article"
              >
                <Share2 className="w-3.5 h-3.5" />
                <span>{copied ? "Lien copié !" : "Partager"}</span>
              </button>
            </div>

            {/* Title & Subtitle */}
            <div className="space-y-3">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-slate-900 tracking-tight leading-tight">
                {article.title}
              </h1>
              <p className="text-base sm:text-xl text-slate-600 leading-relaxed font-normal">
                {article.subtitle}
              </p>
            </div>

            {/* Author bar */}
            {(() => {
              const authorImg = getAuthorImage(article.author, article.authorImage);
              return (
                <div className="flex items-center gap-3.5 pt-4 border-t border-slate-100">
                  {authorImg ? (
                    <img
                      src={authorImg}
                      alt={article.author}
                      className="w-12 h-12 rounded-full object-cover border-2 border-amber-400/80 shadow-md shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm shrink-0">
                      <User className="w-5 h-5 text-amber-400" />
                    </div>
                  )}
                  <div>
                    <div className="text-sm sm:text-base font-bold text-slate-900">
                      {article.author}
                    </div>
                    <div className="text-xs text-slate-500">
                      {article.authorRole}
                    </div>
                  </div>
                </div>
              );
            })()}
          </header>

          {/* Scripture Callout Highlight */}
          <div className="bg-gradient-to-r from-amber-50 via-amber-50/70 to-orange-50 border-l-4 border-amber-500 rounded-2xl p-6 sm:p-8 shadow-sm space-y-2 relative">
            <Quote className="w-8 h-8 text-amber-300/80 absolute top-4 right-4 pointer-events-none" />
            <p className="text-base sm:text-lg text-slate-900 font-serif italic leading-relaxed">
              « {article.bibleVerse.text} »
            </p>
            <div className="text-xs sm:text-sm font-bold text-amber-800 tracking-wide uppercase">
              — {article.bibleVerse.reference}
            </div>
          </div>

          {/* Article Editorial Body */}
          <div className="bg-white rounded-3xl p-6 sm:p-12 shadow-lg border border-slate-200/90 space-y-10 text-slate-800 text-base sm:text-lg leading-relaxed">
            {article.sections.map((section, idx) => (
              <section key={idx} className="space-y-4">
                {section.heading && (
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 pt-2 border-b border-slate-100 pb-2">
                    {section.heading}
                  </h2>
                )}
                {section.paragraphs.map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-slate-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            {/* Tags footer */}
            <div className="pt-6 border-t border-slate-100 space-y-3">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" />
                Mots-clés & Thématiques :
              </div>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Pastoral Council Bio Signature */}
          <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-8 space-y-3">
            <h3 className="text-base sm:text-lg font-bold text-amber-400">
              À propos de l'auteur & de la ligne doctrinale
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Cet article s'inscrit dans la mission pastorale d'édification de
              l'<strong>Église de Dieu Garizim</strong>, communauté chrétienne
              évangélique présente à Montréal (11800 4e Avenue) et sur la
              Rive-Sud (2600 Rue Grant, Longueuil). Les enseignements publiés
              sont soumis à l'autorité souveraine des Saintes Écritures et visent
              à fortifier les familles chrétiennes dans leur marche avec Dieu.
            </p>
          </div>

          {/* Related Articles Section */}
          {relatedArticles.length > 0 && (
            <div className="space-y-4 pt-4">
              <h3 className="text-xl font-bold text-slate-900">
                Poursuivre votre lecture
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/articles/${rel.slug}`}
                    onClick={() => scrollToTop()}
                    className="bg-slate-700/80 text-white p-5 rounded-2xl border border-slate-600/70 shadow-sm hover:shadow-lg hover:border-slate-500/80 hover:bg-slate-700/95 transition-all flex flex-col justify-between space-y-3 group"
                  >
                    <div className="space-y-1.5">
                      <span className="text-[11px] font-bold text-slate-100 bg-slate-800/60 border border-slate-600/70 px-2 py-0.5 rounded-full">
                        {rel.category}
                      </span>
                      <h4 className="text-sm font-bold text-white group-hover:text-amber-200 transition-colors line-clamp-2">
                        {rel.title}
                      </h4>
                    </div>
                    <div className="text-[11px] text-slate-300 flex items-center justify-between pt-2 border-t border-slate-600/60">
                      <span>{rel.date}</span>
                      <span className="font-semibold text-amber-300 group-hover:text-amber-200">
                        Lire &rarr;
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back button */}
          <div className="text-center pt-4">
            <button
              onClick={() => {
                scrollToTop();
                navigate("/articles");
              }}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs sm:text-sm font-bold transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Voir tous les articles & réflexions</span>
            </button>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default PageArticleDetail;
